import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Alert,
    Box,
    CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { importTimeoffRequests, ImportedRequest, ImportRequestsResult } from '../dataProvider';

interface ImportRequestsDialogProps {
    open: boolean;
    onClose: () => void;
}

const REQUIRED_COLUMNS = ['Name', 'Email', 'LeaveType', 'StartDate', 'EndDate'];

/**
 * Sample CSV content offered via the "Download Sample CSV" button, reflecting the
 * trimmed column set (Name, Email, LeaveType, StartDate, EndDate, Comments,
 * NumberOfPaidDays, OptionalLeaveDays, NumberOfUnpaidDays, LeaveUnitsType).
 */
const SAMPLE_CSV = `Name,Email,LeaveType,StartDate,EndDate,Comments,NumberOfPaidDays,OptionalLeaveDays,NumberOfUnpaidDays,LeaveUnitsType
Alice Smith,alice@example.com,Annual Leave,2024-01-10,2024-01-12,Holiday,2,0,0,Day(s)
Bob Jones,bob@example.com,Unpaid Leave,2024-02-01,2024-02-03,,0,0,2,Day(s)`;

/**
 * Strips the word "Leave" (case-sensitive) from a LeaveType string and trims
 * the result. For example, "Annual Leave" becomes "Annual".
 *
 * @param leaveType - The raw LeaveType value from the CSV.
 * @returns The cleaned string with "Leave" removed (case-insensitive) and whitespace trimmed.
 */
export function stripLeaveWord (leaveType: string): string {
    return leaveType.replace(/\bLeave\b/gi, '').trim();
}

/**
 * Parses a CSV text string into an array of {@link ImportedRequest} objects.
 *
 * Expected columns (case-sensitive): Name, Email, LeaveType, StartDate, EndDate,
 * Comments, NumberOfPaidDays, OptionalLeaveDays, NumberOfUnpaidDays, LeaveUnitsType.
 * Any other columns present in the file (e.g. legacy EmployeeId, Family, Function,
 * ManagerName, HRPartnerName) are ignored.
 *
 * Returns `null` if any required column is missing from the header.
 *
 * @param text - Raw CSV text content.
 * @returns Array of parsed rows, or `null` on a missing-column error.
 */
export function parseRequestsCsv (text: string): ImportedRequest[] | null {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 1) return null;

    const header = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    for (const col of REQUIRED_COLUMNS) {
        if (!header.includes(col)) return null;
    }

    const rows: ImportedRequest[] = [];
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Simple CSV splitting that handles quoted fields
        const values: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let j = 0; j < line.length; j++) {
            const ch = line[j];
            if (ch === '"') {
                if (inQuotes && line[j + 1] === '"') {
                    current += '"';
                    j++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === ',' && !inQuotes) {
                values.push(current);
                current = '';
            } else {
                current += ch;
            }
        }
        values.push(current);

        const row: Record<string, string> = {};
        header.forEach((h, idx) => {
            row[h] = (values[idx] ?? '').trim();
        });

        rows.push({
            name: row['Name'] ?? '',
            email: row['Email'] ?? '',
            leave_type: stripLeaveWord(row['LeaveType'] ?? ''),
            start_date: row['StartDate'] ?? '',
            end_date: row['EndDate'] ?? '',
            comments: row['Comments'],
            number_of_paid_days: row['NumberOfPaidDays'],
            optional_leave_days: row['OptionalLeaveDays'],
            number_of_unpaid_days: row['NumberOfUnpaidDays'],
            leave_units_type: row['LeaveUnitsType'],
        });
    }
    return rows;
}

/** Dialog component for uploading a CSV of leave requests for import. */
export const ImportRequestsDialog: React.FC<ImportRequestsDialogProps> = ({ open, onClose }) => {
    const notify = useNotify();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [rows, setRows] = useState<ImportedRequest[] | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [preview, setPreview] = useState<ImportRequestsResult | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    /**
     * Triggers a browser download of {@link SAMPLE_CSV} as `leave_requests_sample.csv`,
     * giving users a ready-to-fill template with the trimmed column set.
     */
    const handleDownloadSample = () => {
        const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'leave_requests_sample.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const fetchPreview = async (parsedRows: ImportedRequest[]) => {
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const result = await importTimeoffRequests(parsedRows, true);
            setPreview(result);
        } catch (error: any) {
            const errorMessage =
                error?.body?.message || error?.message || 'Failed to preview import';
            setPreviewError(errorMessage);
            setPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target?.result as string;
            const parsed = parseRequestsCsv(text);
            if (!parsed) {
                setParseError(
                    `Invalid CSV. Required columns: ${REQUIRED_COLUMNS.join(', ')}`
                );
                setRows(null);
                setPreview(null);
            } else {
                setParseError(null);
                setRows(parsed);
                setPreview(null);
                void fetchPreview(parsed);
            }
        };
        reader.readAsText(file);
    };

    const handleClose = () => {
        setRows(null);
        setParseError(null);
        setPreview(null);
        setPreviewError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose();
    };

    const handleConfirm = async () => {
        if (!rows || rows.length === 0) return;
        setSubmitting(true);
        try {
            const result = await importTimeoffRequests(rows, false);
            const msg = `Import complete: ${result.imported} request(s) imported.`;
            notify(msg, { type: 'success' });
            if (result.duplicates && result.duplicates.length > 0) {
                notify(`${result.duplicates.length} duplicate request(s) skipped.`, { type: 'warning' });
            }
            if (result.warnings && result.warnings.length > 0) {
                result.warnings.forEach((w) => notify(w, { type: 'warning' }));
            }
            handleClose();
        } catch (error: any) {
            const errorMessage =
                error?.body?.message || error?.message || 'Import failed';
            notify(errorMessage, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const canConfirm = !!rows && rows.length > 0 && !previewLoading && !previewError && !!preview && !submitting;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Import Leave Requests</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Upload a CSV file with columns:{' '}
                        <strong>Name, Email, LeaveType, StartDate, EndDate</strong>{' '}
                        (plus optional{' '}
                        <em>Comments, NumberOfPaidDays, OptionalLeaveDays, NumberOfUnpaidDays,
                        LeaveUnitsType</em>).
                        The word &ldquo;Leave&rdquo; will be stripped from the LeaveType value
                        (e.g.&nbsp;&ldquo;Annual Leave&rdquo;&nbsp;&rarr;&nbsp;&ldquo;Annual&rdquo;).
                        Uploading first previews balance changes below for review before anything
                        is saved.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                        />
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleDownloadSample}
                        >
                            Download Sample CSV
                        </Button>
                    </Box>
                </Box>

                {parseError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {parseError}
                    </Alert>
                )}

                {rows && rows.length > 0 && (
                    <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Preview ({rows.length} row{rows.length !== 1 ? 's' : ''})
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Leave Type</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.leave_type}</TableCell>
                                            <TableCell>{row.start_date}</TableCell>
                                            <TableCell>{row.end_date}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {previewLoading && (
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Calculating balance changes…</Typography>
                    </Box>
                )}

                {previewError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {previewError}
                    </Alert>
                )}

                {preview && (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Review: {preview.imported} to import
                            {preview.duplicates.length > 0 && `, ${preview.duplicates.length} duplicate(s) skipped`}
                        </Typography>

                        {preview.warnings.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {preview.warnings.map((w, idx) => (
                                    <div key={idx}>{w}</div>
                                ))}
                            </Alert>
                        )}

                        {preview.duplicates.length > 0 && (
                            <Box mb={2}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Duplicate requests (already imported, will be skipped)
                                </Typography>
                                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Leave Type</TableCell>
                                                <TableCell>Start Date</TableCell>
                                                <TableCell>End Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {preview.duplicates.map((d, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{d.email}</TableCell>
                                                    <TableCell>{d.leave_type}</TableCell>
                                                    <TableCell>{d.start_date}</TableCell>
                                                    <TableCell>{d.end_date}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {preview.balanceDiffs && preview.balanceDiffs.length > 0 && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Balance changes
                                </Typography>
                                <TableContainer component={Paper} sx={{ maxHeight: 250 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Leave Type</TableCell>
                                                <TableCell>Year</TableCell>
                                                <TableCell>Current Balance</TableCell>
                                                <TableCell>New Balance</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {preview.balanceDiffs.map((d, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{d.name}</TableCell>
                                                    <TableCell>{d.leave_type}</TableCell>
                                                    <TableCell>{d.year}</TableCell>
                                                    <TableCell>{d.currentBalance}</TableCell>
                                                    <TableCell>{d.newBalance}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!canConfirm}
                    startIcon={submitting ? <CircularProgress size={16} /> : undefined}
                >
                    {submitting ? 'Importing…' : 'Confirm Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
